import type { TimesheetWeekHoursDto } from 'src/api/queries/timesheets/useTimesheetWeekHours';
import type { PayCodeDto } from 'src/api/queries';
import {
  TIMESHEET_DAY_KEYS,
  TimesheetDayKey,
} from 'src/app/timesheets/timesheetGrid';

export type IDayHours = Record<TimesheetDayKey, number>;

export interface IPayCodeHours {
  payCodeId: string;
  payCodeName: string;
  hours: number;
}

export interface IPayCodeDayHours extends IPayCodeHours, IDayHours {}

export interface ITimesheetWeekHoursTotals {
  days: IDayHours;
  total: number;
}

export interface ITimesheetWeekHoursOtherDetail
  extends ITimesheetWeekHoursTotals {
  jobLabel: string;
  payCodeName: string;
}

export interface ITimesheetWeekHoursPayCodeRow
  extends ITimesheetWeekHoursTotals {
  payCodeName: string;
}

export interface ITimesheetWeekHours {
  thisTimesheet: ITimesheetWeekHoursTotals;
  other: ITimesheetWeekHoursTotals & {
    timesheetCount: number;
    details: ITimesheetWeekHoursOtherDetail[];
  };
  week: ITimesheetWeekHoursTotals & {
    payCodes: ITimesheetWeekHoursPayCodeRow[];
  };
}

/** The bucket the backend uses for hours whose row carries no pay code. */
export const UNASSIGNED_PAY_CODE_NAME = 'ST';

const RESIDUAL_EPSILON = 0.0001;

const zeroDays = (): IDayHours => ({
  sat: 0,
  sun: 0,
  mon: 0,
  tue: 0,
  wed: 0,
  thu: 0,
  fri: 0,
});

const addDaysInto = (target: IDayHours, source: IDayHours) => {
  TIMESHEET_DAY_KEYS.forEach((day) => {
    target[day] += source[day];
  });
};

const sumDays = (a: IDayHours, b: IDayHours): IDayHours => {
  const result = zeroDays();
  addDaysInto(result, a);
  addDaysInto(result, b);
  return result;
};

const hasHours = (entry: ITimesheetWeekHoursTotals) =>
  entry.total !== 0 || TIMESHEET_DAY_KEYS.some((day) => entry.days[day] !== 0);

const emptyTotals = (): ITimesheetWeekHoursTotals => ({
  days: zeroDays(),
  total: 0,
});

const accumulate = (
  bucket: Map<string, ITimesheetWeekHoursTotals>,
  name: string,
  days: IDayHours,
  total: number,
) => {
  const existing = bucket.get(name) ?? emptyTotals();
  addDaysInto(existing.days, days);
  existing.total += total;
  bucket.set(name, existing);
};

export interface IBuildTimesheetWeekHoursArgs {
  thisHoursByPayCode: IPayCodeDayHours[];
  thisTotalsByDay: IDayHours;
  thisTotalHours: number;
  weekHours: TimesheetWeekHoursDto | undefined;
  excludeJobId: number | undefined;
  payCodes: PayCodeDto[];
}

/** @registry Builds the timesheet week hours panel model from live timesheet card state plus the user's other timesheets for the same week. */
export const buildTimesheetWeekHours = ({
  thisHoursByPayCode,
  thisTotalsByDay,
  thisTotalHours,
  weekHours,
  excludeJobId,
  payCodes,
}: IBuildTimesheetWeekHoursArgs): ITimesheetWeekHours => {
  const liveByName = new Map<string, ITimesheetWeekHoursTotals>();
  const assigned = emptyTotals();

  thisHoursByPayCode.forEach((entry) => {
    accumulate(liveByName, entry.payCodeName, entry, entry.hours);
    addDaysInto(assigned.days, entry);
    assigned.total += entry.hours;
  });

  // Live rows with no pay code are absent from thisHoursByPayCode but present
  // in the card totals; the backend files them under UNASSIGNED_PAY_CODE_NAME.
  const residualDays = zeroDays();
  TIMESHEET_DAY_KEYS.forEach((day) => {
    const residual = thisTotalsByDay[day] - assigned.days[day];
    if (residual > RESIDUAL_EPSILON) {
      residualDays[day] = residual;
    }
  });
  const residualTotal = thisTotalHours - assigned.total;
  const residual: ITimesheetWeekHoursTotals = {
    days: residualDays,
    total: residualTotal > RESIDUAL_EPSILON ? residualTotal : 0,
  };
  if (hasHours(residual)) {
    accumulate(
      liveByName,
      UNASSIGNED_PAY_CODE_NAME,
      residual.days,
      residual.total,
    );
  }

  const otherTimesheets = (weekHours?.timesheets ?? []).filter(
    (timesheet) => excludeJobId == null || timesheet.jobId !== excludeJobId,
  );

  const otherByName = new Map<string, ITimesheetWeekHoursTotals>();
  const otherTotals = emptyTotals();
  const details: ITimesheetWeekHoursOtherDetail[] = [];

  otherTimesheets.forEach((timesheet) => {
    const jobLabel =
      timesheet.jobName ??
      (timesheet.jobId == null ? 'Unknown job' : `Job ${timesheet.jobId}`);

    timesheet.hoursByPayCode.forEach((entry) => {
      const days = zeroDays();
      TIMESHEET_DAY_KEYS.forEach((day) => {
        const value = entry[day];
        days[day] = Number.isFinite(value) ? value : 0;
      });
      details.push({
        jobLabel,
        payCodeName: entry.payCodeName,
        days,
        total: entry.hours,
      });
      accumulate(otherByName, entry.payCodeName, days, entry.hours);
      addDaysInto(otherTotals.days, days);
      otherTotals.total += entry.hours;
    });
  });

  const orderedNames: string[] = [];
  const seen = new Set<string>();
  payCodes.forEach((payCode) => {
    if (!seen.has(payCode.name)) {
      seen.add(payCode.name);
      orderedNames.push(payCode.name);
    }
  });
  liveByName.forEach((_, name) => {
    if (!seen.has(name)) {
      seen.add(name);
      orderedNames.push(name);
    }
  });
  orderedNames.push(
    ...[...otherByName.keys()]
      .filter((name) => !seen.has(name))
      .sort((a, b) => a.localeCompare(b)),
  );

  const payCodeRows = orderedNames
    .map((payCodeName) => {
      const live = liveByName.get(payCodeName) ?? emptyTotals();
      const other = otherByName.get(payCodeName) ?? emptyTotals();
      return {
        payCodeName,
        days: sumDays(live.days, other.days),
        total: live.total + other.total,
      };
    })
    .filter(hasHours);

  return {
    thisTimesheet: {
      days: { ...thisTotalsByDay },
      total: thisTotalHours,
    },
    other: {
      timesheetCount: otherTimesheets.length,
      days: otherTotals.days,
      total: otherTotals.total,
      details,
    },
    week: {
      days: sumDays(thisTotalsByDay, otherTotals.days),
      total: thisTotalHours + otherTotals.total,
      payCodes: payCodeRows,
    },
  };
};
